import { Injectable } from '@angular/core';
import { BaseProvider } from './base';
import { Events } from '../utils/events';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';

export enum ZoomControlState {
    None,
    ZoomIn,
    ZoomOut
}

/** A generic coordinate object for use in zoomable SVG elements.
 * The lengthier name should help avoid confusion with Canvas and PaperJS interfaces.
 */
export interface ZoomControlsPoint {
    x: number;
    y: number;
}

export const ZOOM_SCALE_FACTOR = 1.2;

// There are some bugs with our zoom implementation that pop up when the user is especially zoomed in.
// Seting a maximum zoom allows us to dodge those, though we'll likely still want one even if
// those bugs are fixed.
export const MAXIMUM_ALLOWED_ZOOM = 5;

@Injectable()
export class ZoomControlsProvider extends BaseProvider {
    private zoomFactor = 1;
    private zoomOffset: ZoomControlsPoint = { x: 0, y: 0 };
    public zoomFactorSubject = new BehaviorSubject<number>(1);

    constructor(private events: Events) {
        super();
    }

    public setZoomFactor(newZoomFactor: number) {
        this.zoomFactor = newZoomFactor;
        this.zoomFactorSubject.next(newZoomFactor);
    }

    public setZoomOffset(coordinates: ZoomControlsPoint) {
        this.zoomOffset.x = coordinates.x;
        this.zoomOffset.y = coordinates.y;
    }

    public getBoundedCoordinates(event: MouseEvent, target: SVGElement) {
        if (!target) {
            return {
                x: 0,
                y: 0
            };
        }

        // First, we need some metadata about where we clicked within the element.
        const boundingBox = target.getBoundingClientRect();

        let x = event.clientX - boundingBox.left;
        let y = event.clientY - boundingBox.top;

        if (x < 0) {
            x = 0;
        }

        if (y < 0) {
            y = 0;
        }

        if (x > boundingBox.width) {
            x = boundingBox.width;
        }

        if (y > boundingBox.height) {
            y = boundingBox.height;
        }

        return { x, y };
    }

    /**
     * Take a mouse event (either a click or a mouse motion on the live feed image)
     * and return its coordinates adjusted for the size of the input camera.
     * This function is not currently used, but it will come in handy as we continue to add more SVG zoom functionality to Brain Builder.
     * @param event A mouse event
     * @returns An object with a calibrated x/y coordinate pair
     */
    public getZoomAdjustedCoordinates(event: MouseEvent, target: SVGElement, naturalWidth: number): ZoomControlsPoint {
        if (!target) {
            return {
                x: 0,
                y: 0
            };
        }

        const boundingBox = target.getBoundingClientRect();
        const zoomFactor = naturalWidth / boundingBox.width;

        const { x, y } = this.getBoundedCoordinates(event, target);

        return { x: x * zoomFactor, y: y * zoomFactor };
    }

    /**
     * Get how much the browser is zooming in or out in order to fit the current image in a zoomable SVG container.x
     * @param target The zoomable element the user clicked on.
     * @param naturalWidth The actual width of the current image in pixels.
     * @returns
     */
    public getZoomFactor(target: SVGElement, naturalWidth: number): number {
        const boundingBox = target.getBoundingClientRect();
        return naturalWidth / boundingBox.width;
    }

    /**
     * If the user has selected the zoom in/out controls and clicks on the image,
     * or if they hover over the image and move their scroll wheel, adjust how zoomed in they are,
     * center the viewport on where they clicked, and finally adjust the viewport to make sure that we're not too far outside the image bounds.
     * @param event The mouse event that caused this function to fire.
     * @param width The width of the image we're zooming in the user's browser viewport
     * @param height The height of the image we're zooming in the user's browser viewport
     * @param currentZoomControlState Which zoom tool is currently being used. Only used for clicking when zoom controls are enabled.
     */
    public onSVGImageMouseEvent(
        event: MouseEvent | WheelEvent,
        width: number,
        height: number,
        currentZoomControlState?: ZoomControlState
    ): string {
        let transform = '';

        if (
            ('deltaY' in event && currentZoomControlState === ZoomControlState.None) ||
            event.target instanceof SVGElement === false
        ) {
            return transform;
        }

        this.computeNewZoomFactor(event, currentZoomControlState);
        this.computeInitialZoomOffset(event, width, height, currentZoomControlState);
        this.adjustZoomOffsetForAssetView(event);

        // We need to use coordinates based on physical pixels rendered instead of the real size of the image.
        // Attempting to zoom and pan using the actual coordinates will result in inconsistent zoom behavior depending on image dimensions.
        if (this.zoomFactor >= 1) {
            // The results of CSS transforms depend on the order they're listed.
            // Zooming in before translating can, for instance, result in much smaller movements within the image.
            transform = `transform: scale(${this.zoomFactor}) translate(${this.zoomOffset.x}px, ${this.zoomOffset.y}px)`;
        }

        return transform;
    }

    private computeInitialZoomOffset(
        event: MouseEvent | WheelEvent,
        width: number,
        height: number,
        currentZoomControlState?: ZoomControlState
    ) {
        const target = event.target as SVGElement;

        const boundedCoordinates = this.getBoundedCoordinates(event, target);
        const browserImageZoomFactor = this.getZoomFactor(target, width);

        const centerOfImage: ZoomControlsPoint = {
            x: width / (2 * browserImageZoomFactor),
            y: height / (2 * browserImageZoomFactor)
        };

        const centerOfViewPort: ZoomControlsPoint = {
            x: centerOfImage.x + this.zoomOffset.x,
            y: centerOfImage.y + this.zoomOffset.y
        };

        let newCenter;

        // When the user is zooming out, we want to prioritize moving towards the center,
        // so we use a weighted average to help push them closer to the center of the image.
        // This helps avoid edge cases where clamping doesn't fully work when zooming out near the corners,
        // as does increasing how hard we push the further the user zooms in.
        if (currentZoomControlState === ZoomControlState.ZoomIn) {
            newCenter = {
                x: (boundedCoordinates.x + centerOfViewPort.x) / 2,
                y: (boundedCoordinates.y + centerOfViewPort.y) / 2
            };
        } else {
            newCenter = {
                x: (boundedCoordinates.x + centerOfViewPort.x * (1 + this.zoomFactor)) / (2 + this.zoomFactor),
                y: (boundedCoordinates.y + centerOfViewPort.y * (1 + this.zoomFactor)) / (2 + this.zoomFactor)
            };
        }

        const zoomDelta: ZoomControlsPoint = {
            x: centerOfViewPort.x - newCenter.x,
            y: centerOfViewPort.y - newCenter.y
        };

        // Taking a fractional root of our delta helps the user pan more precisely as they zoom in.
        // However, imaginary roots aren't of much use to us, so for negative deltas, we need to work
        // on the absolute value of the change.
        this.zoomOffset = {
            x: zoomDelta.x >= 0 ? Math.pow(zoomDelta.x, 0.9) : Math.pow(Math.abs(zoomDelta.x), 0.9) * -1,
            y: zoomDelta.y >= 0 ? Math.pow(zoomDelta.y, 0.9) : Math.pow(Math.abs(zoomDelta.y), 0.9) * -1
        };
    }

    private adjustZoomOffsetForAssetView(event: MouseEvent | WheelEvent) {
        const target = event.target as SVGElement;

        // In order to keep the user's zoom from spiraling out of control, we need to be able to track the relative dimensions of
        // the asset and its main container.
        const svgContainer = document.querySelector('.svg-container--asset-view');

        // This should be defined as long as we aren't in a very bad state, but we should be able to recover gracefully.
        if (!svgContainer) {
            return this.zoomOffset;
        }

        const assetBoundingBox = target.getBoundingClientRect();
        const containerBoundingBox = svgContainer.getBoundingClientRect();

        // With these dimensions, we can compute how far a user should be able to pan the image before they reach its edges.
        // We need to account for the zoom factor, and dividing by two ensures the inner edge(s) of the image don't move away from
        // the outer edges of the asset view. A positive clamp value means that the corresponding dimension of the image is larger
        // than that of our viewport. A negative value, on the other hand, means the entire width or height of the image still fits
        // in the container.
        const clampValues = {
            width: (assetBoundingBox.width - containerBoundingBox.width) / (2 * this.zoomFactor),
            height: (assetBoundingBox.height - containerBoundingBox.height) / (2 * this.zoomFactor)
        };

        // If we're zoomed in enough, we'll attempt to re-pan the view so that the edge of the area we're zooming towards matches the edge of the viewport.
        // Otherwise, we'll keep the the image centered for any axis that still fits entirely within the container.
        if (clampValues.width > 0) {
            if (this.zoomOffset.x > clampValues.width) {
                this.zoomOffset.x = clampValues.width;
            } else if (this.zoomOffset.x < clampValues.width * -1) {
                this.zoomOffset.x = clampValues.width * -1;
            }
        } else {
            this.zoomOffset.x = 0;
        }

        if (clampValues.height > 0) {
            if (this.zoomOffset.y > clampValues.height) {
                this.zoomOffset.y = clampValues.height;
            } else if (this.zoomOffset.y < clampValues.height * -1) {
                this.zoomOffset.y = clampValues.height * -1;
            }
        } else {
            this.zoomOffset.y = 0;
        }
    }

    private computeNewZoomFactor(event: MouseEvent | WheelEvent, currentZoomControlState?: ZoomControlState) {
        const oldZoomFactor = this.zoomFactorSubject.getValue();
        let zoomFactor = oldZoomFactor;

        // We need to compute the zoom factor based on either the user's scroll delta, or their current mode.
        // Typescript will complain if this property check is moved.
        if ('deltaY' in event) {
            // By decoupling zoom behavior from any sensitivity settings on the user's mousewheel,
            // we ensure more consistent zooming behavior.
            if (event.deltaY < 0) {
                // Scroll up to zoom in.
                zoomFactor = zoomFactor * ZOOM_SCALE_FACTOR;
            } else if (event.deltaY > 0) {
                // Scroll down to zoom out.
                zoomFactor = zoomFactor / ZOOM_SCALE_FACTOR;

                this.reduceZoomOffset();
            }
        } else {
            if (currentZoomControlState === ZoomControlState.ZoomIn) {
                zoomFactor = zoomFactor * ZOOM_SCALE_FACTOR;
            } else if (currentZoomControlState === ZoomControlState.ZoomOut) {
                zoomFactor = zoomFactor / ZOOM_SCALE_FACTOR;

                this.reduceZoomOffset();
            }
        }

        // Guard against edge cases where we manage to zoom out further than should be possible.
        // We also want to prevent users from zooming too far in.
        if (zoomFactor < 1) {
            zoomFactor = 1;
        } else if (zoomFactor > MAXIMUM_ALLOWED_ZOOM) {
            zoomFactor = MAXIMUM_ALLOWED_ZOOM;
        }

        this.setZoomFactor(zoomFactor);
        return zoomFactor;
    }

    // Nudge the user's zoom offset slightly depending on their panning actions (holding SPACE while zoomed in, then moving their mouse) in the SVG container component.
    // Adjusting the offset will prevent users from panning their image outside of the viewport.
    public computePannedZoomOffset(event: MouseEvent, deltaX: number, deltaY: number): string {
        this.zoomOffset.x -= deltaX;
        this.zoomOffset.y -= deltaY;

        this.adjustZoomOffsetForAssetView(event);

        // We need less error trapping here when building our transform, since this function will not fire unless the user is zoomed in.
        const transform = `transform: scale(${this.zoomFactor}) translate(${this.zoomOffset.x}px, ${this.zoomOffset.y}px)`;

        return transform;
    }

    // If the user is zooming out, we should reduce how panned they are, bringing them closer to the center of the image.
    private reduceZoomOffset() {
        this.zoomOffset.x = this.zoomOffset.x / ZOOM_SCALE_FACTOR;
        this.zoomOffset.y = this.zoomOffset.y / ZOOM_SCALE_FACTOR;
    }
}



// WEBPACK FOOTER //
// ./src/providers/zoom_controls.ts